import { useEffect, useState } from 'react';

import { showNotification } from 'shared/ui/Notification';

import IconShare from 'shared/ui/Icon/24/IconShare';

import clsx from 'clsx';
import IconClose from 'shared/ui/Icon/16/IconClose';

import { isMobileTablet } from 'shared/utils/devices';
import { useLocale } from 'shared/hooks/useLocale';
import { fullLocales } from 'schema/locale';
import { BlogSettingsResult } from 'types/studio/blog';
import { FacebookIcon, TwitterIcon, LinkIcon } from '@ag1/ui';
import styles from './SocialShare.module.scss';

interface Props {
  className?: string;
  url: string;
  title: string;
  theme?: BlogSettingsResult['theme'];
}

const SocialShare = ({ className, url, title, theme = 'old' }: Props) => {
  const [opened, setOpen] = useState(false);
  const [hasNativeShare, setNativeShare] = useState(false);
  const locale = useLocale();
  const isMobileTabletDevice = isMobileTablet();

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const openDialog = (href: string) => {
    window.open(
      href,
      'targetWindow',
      'toolbar=no,location=0,status=no,menubar=no,scrollbars=yes,resizable=yes,width=600,height=400'
    );
  };

  const shareTwitter = () => {
    const twitterShareLink = `https://twitter.com/intent/tweet?url=${encodeURIComponent(
      url
    )}&text=${encodeURIComponent(title)}`;

    openDialog(twitterShareLink);
  };

  const shareFacebook = () => {
    const facebookShareLink = `https://facebook.com/sharer/sharer.php?u=${encodeURIComponent(
      url
    )}&t=${title}`;

    openDialog(facebookShareLink);
  };

  const copyLink = () => {
    navigator.clipboard.writeText(url);
    const fullLocale = fullLocales.find((item) => item.locale === locale);
    const notifyTitle =
      fullLocale?.language === 'de' ? 'In die Zwischenablage kopiert' : 'Copied to clipboard';
    showNotification('mutationSuccess', {
      title: notifyTitle
    });
  };

  const nativeShare = () => {
    navigator.share({
      url,
      title
    });
  };

  useEffect(() => {
    if (
      isMobileTabletDevice &&
      typeof window !== 'undefined' &&
      typeof navigator?.canShare === 'function' &&
      navigator.canShare({ url, title })
    ) {
      setNativeShare(true);
    }
  }, [isMobileTabletDevice, title, url]);

  return (
    <div className={clsx(styles.wrapper, opened && styles.opened, className, styles[theme])}>
      {hasNativeShare ? (
        <button
          type="button"
          onClick={nativeShare}
          className={clsx(styles.shareButton, styles[theme])}>
          <IconShare />
        </button>
      ) : (
        <>
          <div className={clsx(styles.socialButtonsWrapper, opened && styles.opened)}>
            <button type="button" onClick={shareFacebook} className={styles.shareButton}>
              <FacebookIcon />
            </button>
            <button type="button" onClick={shareTwitter} className={styles.shareButton}>
              <TwitterIcon />
            </button>
            <button
              type="button"
              onClick={copyLink}
              className={clsx(styles.shareButton, styles.linkButton)}>
              <LinkIcon />
            </button>
            <button
              type="button"
              onClick={handleClose}
              className={clsx(styles.shareButton, styles.closeButton)}>
              <IconClose />
            </button>
          </div>
          <button
            type="button"
            onClick={handleOpen}
            className={clsx(styles.shareButton, opened && styles.opened, styles[theme])}>
            <IconShare />
          </button>
        </>
      )}
    </div>
  );
};

export default SocialShare;
